<script lang="ts" setup>
import { computed, onBeforeMount, provide, ref, watch } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { useRoute, useRouter } from 'vue-router/composables'
import {
  TCreateEventFormComponent,
  TCreateEventFormKey,
  CREATE_EVENT_FORMS,
} from '@/pages/CreateEventProcessingPage/static'
import { useEventEditQuery } from '@/enitites/event/api/queries'
import EventEditLayout from '@/pages/CreateEventProcessingPage/ui/layout/EventEditLayout.vue'

const router = useRouter()
const route = useRoute()

// Get current step from query parameter, default to 'total'
const currentStep = computed<TCreateEventFormKey>(() => {
  const step = route.query.step as TCreateEventFormKey
  return step && step in CREATE_EVENT_FORMS ? step : 'total'
})

const currentScreen = computed<TCreateEventFormComponent>(() => {
  return CREATE_EVENT_FORMS[currentStep.value]
})

// Provide values for EventEditActionButtons compatibility
provide('currentEventStep', ref(0))
provide('totalEventSteps', ref(1))
provide('isCreationFlow', false)

const { data: eventEdit, error, isLoading } = useEventEditQuery(Number(route.params.id))

const getUserDB = computed(() => store.getters.getUserDB)

const handleNext = async () => {
  if (currentStep.value === 'total') {
    // For total form, navigate back to event page
    await router.push({ name: 'Event', params: { id: String(route.params.id) } })
    store.dispatch('showAlertToast', 'eventUpdated')
  } else {
    // For other forms, navigate to total (forms handle their own updates)
    router.push({
      name: 'EventEdit',
      params: { id: String(route.params.id) },
      query: { step: 'total' },
    })
  }
}

const handleBack = () => {
  if (currentStep.value === 'total') {
    // For total form, go back to event page
    router.push({ name: 'Event', params: { id: String(route.params.id) } })
  } else {
    // For other forms, go back to total without saving
    router.push({
      name: 'EventEdit',
      params: { id: String(route.params.id) },
      query: { step: 'total' },
    })
  }
}

const handleCancel = () => {
  router.push({ name: 'Event', params: { id: String(route.params.id) } })
}

onBeforeMount(() => {
  if (!getUserDB.value) {
    router.push({ name: 'EventsGrid' })
  }
})

watch(error, () => {
  if (error.value) {
    store.dispatch('showAlertToast', {
      type: 'error',
      message: error.value.message,
    })
    router.push({ name: 'Event', params: { id: String(route.params.id) } })
  }
})
</script>

<template>
  <EventEditLayout
    :title="currentScreen.pageTitle === 'Create Event' ? 'Edit Event' : currentScreen.pageTitle"
    :description="currentScreen.pageDescription"
    :loading="isLoading"
    :error="error?.message"
  >
    <component
      :is="currentScreen.form"
      v-if="eventEdit"
      :component="currentScreen.form"
      :event-data="eventEdit"
      @next="handleNext"
      @back="handleBack"
      @cancel="handleCancel"
    />
  </EventEditLayout>
</template>
